/**
 * Card Image Swiper — event-delegated, works with AJAX-loaded content.
 * Single set of listeners on document handles all product cards.
 */
(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 989px)';
  var CONTROLS_CLASS = 'card-swiper--controls-visible';
  var DOT_ACTIVE = 'card-swiper-dot--active';
  var HIDE_DELAY = 3000; // ms before mobile arrows auto-hide

  // Per-wrapper timer for mobile auto-hide
  var hideTimers = new WeakMap();

  /* ---- Helpers ---- */

  function getTrack(wrapper) {
    return wrapper.querySelector('.card-swiper-track');
  }

  function getSlideCount(track) {
    return parseInt(track.getAttribute('data-slide-count'), 10) || 1;
  }

  function getCurrentIndex(track) {
    return parseInt(track.getAttribute('data-current'), 10) || 0;
  }

  function goToSlide(wrapper, index) {
    var track = getTrack(wrapper);
    if (!track) return;

    var count = getSlideCount(track);
    // Wrap around
    if (index < 0) index = count - 1;
    if (index >= count) index = 0;

    track.setAttribute('data-current', index);
    track.style.transform = 'translateX(-' + (index * 100) + '%)';

    // Both arrows always enabled (infinite loop)
    var prevBtn = wrapper.querySelector('.card-swiper-arrow--prev');
    var nextBtn = wrapper.querySelector('.card-swiper-arrow--next');
    if (prevBtn) prevBtn.removeAttribute('disabled');
    if (nextBtn) nextBtn.removeAttribute('disabled');

    // Update dot indicators
    var dots = wrapper.querySelectorAll('.card-swiper-dot');
    dots.forEach(function (dot, i) {
      dot.classList.toggle(DOT_ACTIVE, i === index);
    });
  }

  function showMobileControls(wrapper) {
    if (!window.matchMedia(MOBILE_MQ).matches) return;
    wrapper.classList.add(CONTROLS_CLASS);

    // Clear previous timer
    var prev = hideTimers.get(wrapper);
    if (prev) clearTimeout(prev);

    // Auto-hide after delay
    var timer = setTimeout(function () {
      wrapper.classList.remove(CONTROLS_CLASS);
    }, HIDE_DELAY);
    hideTimers.set(wrapper, timer);
  }

  /* ---- Event Delegation ---- */

  document.addEventListener('click', function (e) {
    var wrapper, track, idx;

    // Arrow: prev
    var prevArrow = e.target.closest('.card-swiper-arrow--prev');
    if (prevArrow) {
      e.preventDefault();
      e.stopPropagation();
      wrapper = prevArrow.closest('.product-image-wrapper');
      if (!wrapper) return;
      track = getTrack(wrapper);
      if (!track) return;
      idx = getCurrentIndex(track);
      goToSlide(wrapper, idx - 1);
      showMobileControls(wrapper);
      return;
    }

    // Arrow: next
    var nextArrow = e.target.closest('.card-swiper-arrow--next');
    if (nextArrow) {
      e.preventDefault();
      e.stopPropagation();
      wrapper = nextArrow.closest('.product-image-wrapper');
      if (!wrapper) return;
      track = getTrack(wrapper);
      if (!track) return;
      idx = getCurrentIndex(track);
      goToSlide(wrapper, idx + 1);
      showMobileControls(wrapper);
      return;
    }

    // Dot click
    var dot = e.target.closest('.card-swiper-dot');
    if (dot) {
      e.preventDefault();
      e.stopPropagation();
      wrapper = dot.closest('.product-image-wrapper');
      if (!wrapper) return;
      var dotIndex = parseInt(dot.getAttribute('data-index'), 10);
      if (!isNaN(dotIndex)) {
        goToSlide(wrapper, dotIndex);
        showMobileControls(wrapper);
      }
      return;
    }

    // Skip wishlist button taps — let them pass through to the wishlist handler
    if (e.target.closest('.wishlist-btn')) return;

    // Mobile + Tablet: tap on image area to show controls
    if (window.matchMedia(MOBILE_MQ).matches) {
      wrapper = e.target.closest('[data-card-swiper]');
      if (wrapper && !wrapper.classList.contains(CONTROLS_CLASS)) {
        // First tap: show controls and prevent navigation
        e.preventDefault();
        e.stopPropagation();
        showMobileControls(wrapper);
      }
    }
  }, true); // capture phase so we can prevent <a> navigation on mobile first-tap

  /* ---- Touch Swipe (mobile) ---- */
  // Direction-locking: once we decide the swipe is horizontal inside a
  // card swiper, we preventDefault on touchmove to stop the parent
  // section slider from scrolling. Vertical swipes pass through normally.

  var SWIPE_THRESHOLD = 20;  // min px to count as a completed swipe
  var LOCK_THRESHOLD = 8;    // min px movement before we lock direction
  // 'none' = undecided, 'horizontal' = card swiper owns it, 'vertical' = page owns it
  var touch = { startX: 0, startY: 0, wrapper: null, direction: 'none' };

  document.addEventListener('touchstart', function (e) {
    if (!window.matchMedia(MOBILE_MQ).matches) return;
    var wrapper = e.target.closest('[data-card-swiper]');
    if (!wrapper) return;
    // Only claim touches that have >1 slide to swipe through
    var track = getTrack(wrapper);
    if (!track || getSlideCount(track) < 2) return;

    var t = e.touches[0];
    touch.startX = t.clientX;
    touch.startY = t.clientY;
    touch.wrapper = wrapper;
    touch.direction = 'none';
  }, { passive: true });

  // touchmove must be NON-passive so we can preventDefault
  document.addEventListener('touchmove', function (e) {
    if (!touch.wrapper) return;
    // Already decided vertical — let the browser handle it
    if (touch.direction === 'vertical') return;

    var t = e.touches[0];
    var dx = t.clientX - touch.startX;
    var dy = t.clientY - touch.startY;

    // Haven't moved enough to decide yet
    if (touch.direction === 'none') {
      if (Math.abs(dx) < LOCK_THRESHOLD && Math.abs(dy) < LOCK_THRESHOLD) return;
      // Lock direction
      touch.direction = Math.abs(dx) >= Math.abs(dy) ? 'horizontal' : 'vertical';
    }

    // Horizontal lock — prevent parent section scroller
    if (touch.direction === 'horizontal') {
      e.preventDefault();
    }
  }, { passive: false });

  document.addEventListener('touchend', function (e) {
    if (!touch.wrapper || touch.direction !== 'horizontal') {
      touch.wrapper = null;
      touch.direction = 'none';
      return;
    }

    var t = e.changedTouches[0];
    var dx = t.clientX - touch.startX;
    var wrapper = touch.wrapper;

    // Reset state before processing
    touch.wrapper = null;
    touch.direction = 'none';

    if (Math.abs(dx) < SWIPE_THRESHOLD) return;

    var track = getTrack(wrapper);
    if (!track) return;
    var idx = getCurrentIndex(track);

    if (dx < 0) {
      goToSlide(wrapper, idx + 1); // swipe left → next
    } else {
      goToSlide(wrapper, idx - 1); // swipe right → prev
    }
    showMobileControls(wrapper);
  }, { passive: true });

  // Clean up if touch is cancelled (e.g. incoming call)
  document.addEventListener('touchcancel', function () {
    touch.wrapper = null;
    touch.direction = 'none';
  }, { passive: true });

  /* ---- Reduced motion ---- */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    var style = document.createElement('style');
    style.textContent = '.card-swiper-track { transition-duration: 0s !important; }';
    document.head.appendChild(style);
  }
})();

/* ============================================================
   Deferred image hydration (global)
   ============================================================
   WHY HERE: swiper slides 2-5 (and secondary hover images) render
   with data-src/data-srcset so they don't compete with the LCP
   candidate during the initial paint race. They must be promoted to
   real src/srcset by a hydration pass. That pass historically lived
   ONLY inside sections/main-collection-product-grid.liquid, so on
   non-collection pages (homepage, search, related-product sliders)
   the section is absent and slides 2-5 never hydrated — the reported
   "first image loads, the rest stay blank" bug.

   This module mirrors that proven logic. It lives in card-swiper.js
   because that asset is loaded globally on every page (via
   layout/theme.liquid with defer), so every page gets hydration.

   It shares the SAME run-once guards with the collection-grid inline
   copy — window._collectionImgHydrated (global), img._mvsHydrated
   (per-image), card._mvsCardHydrated (per-card) — so when BOTH this
   script and the collection-grid script are present on a page,
   hydration runs only once and the two code paths are mutually
   idempotent (no double work).
   ============================================================ */
(function () {
  'use strict';

  if (window._collectionImgHydrated) return;
  var hydrated = false;

  function hydrateImg(img) {
    if (img._mvsHydrated) return;
    img._mvsHydrated = true;
    var ds = img.getAttribute('data-src');
    var dss = img.getAttribute('data-srcset');
    if (ds) { img.setAttribute('src', ds); img.removeAttribute('data-src'); }
    if (dss) { img.setAttribute('srcset', dss); img.removeAttribute('data-srcset'); }
  }

  function hydrateAll() {
    if (hydrated) return;
    hydrated = true;
    window._collectionImgHydrated = true;
    var imgs = document.querySelectorAll('img[data-src]');
    // Stagger in batches of 6 to avoid a single bandwidth burst on slow connections.
    var i = 0;
    function next() {
      var batch = Array.prototype.slice.call(imgs, i, i + 6);
      if (!batch.length) return;
      batch.forEach(hydrateImg);
      i += 6;
      if (i < imgs.length) setTimeout(next, 80);
    }
    next();
  }

  function scheduleHydration() {
    // Trigger 1: PerformanceObserver — fires when an LCP entry is recorded.
    if ('PerformanceObserver' in window) {
      try {
        // Only react to LCP entries recorded after we begin observing, so an
        // already-buffered entry (e.g. when this script is injected/parsed
        // after the LCP has painted) doesn't trigger an instant hydration that
        // would defeat the post-LCP deferral. (Hardening over the collection-grid
        // copy, which reacts to the first entry unconditionally.)
        var observeStart = performance.now();
        var po = new PerformanceObserver(function (list) {
          var fresh = list.getEntries().some(function (entry) {
            return entry.startTime >= observeStart;
          });
          if (fresh) {
            po.disconnect();
            (window.requestIdleCallback || function (cb) { setTimeout(cb, 0); })(hydrateAll);
          }
        });
        po.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (e) { /* fallback below */ }
    }
    // Trigger 2: safety net — fires regardless of LCP observer support.
    if (document.readyState === 'complete') {
      setTimeout(hydrateAll, 1500);
    } else {
      window.addEventListener('load', function () { setTimeout(hydrateAll, 1500); });
    }
  }

  // Per-card on-interact fallback: if the user hovers/touches a card before
  // global hydration completes, hydrate that card's deferred images instantly
  // so hover/swipe behaviour never appears broken.
  function hydrateCard(card) {
    if (!card || card._mvsCardHydrated) return;
    card._mvsCardHydrated = true;
    card.querySelectorAll('img[data-src]').forEach(hydrateImg);
  }

  // mouseenter doesn't bubble — must use capture phase.
  document.addEventListener('mouseenter', function (e) {
    var card = e.target.closest && e.target.closest('.custom-product-card');
    if (card) hydrateCard(card);
  }, true);

  document.addEventListener('touchstart', function (e) {
    var card = e.target.closest && e.target.closest('.custom-product-card');
    if (card) hydrateCard(card);
  }, { passive: true, capture: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleHydration);
  } else {
    scheduleHydration();
  }
})();
